import { Avatar, AvatarFallback } from "@/components/avatar";
import { Button } from "@/components/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/dropdown-menu";
import { Editor } from "@/components/editor";
import { Input } from "@/components/input";
import {
  useCreateExperimentNote,
  useDeleteExperimentNote,
  useExperimentNotes,
  useUpdateExperimentNote,
  useUser,
} from "@/http/api-hooks";
import { ExperimentNote } from "@/http/api-types";
import { cn } from "@/lib/utils";
import { formatDistanceToNow } from "date-fns";
import { CornerDownRight, Edit2, MessageSquare, MoreHorizontal, Reply, Send, Trash2, X } from "lucide-react";
import { useCallback, useEffect, useRef, useState, type FormEvent } from "react";
import { useLocation } from "react-router-dom";
import { hasLocalImageReference } from "./experiment-notes-utils";

function getErrorMessage(error: unknown): string {
  if (error && typeof error === "object") {
    if ("response" in error && error.response && typeof error.response === "object") {
      const response = error.response as { data?: { error?: string; errors?: { message: string }[] } };
      if (response.data?.error) {
        return response.data.error;
      }
      if (response.data?.errors?.[0]?.message) {
        return response.data.errors[0].message;
      }
    }
    if ("message" in error && typeof error.message === "string") {
      return error.message;
    }
  }
  return "An unexpected error occurred";
}

function getInitials(name: string): string {
  return name
    .split(" ")
    .map(part => part[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
}

function formatTimestamp(dateString: string): string {
  const date = new Date(dateString);
  return formatDistanceToNow(date, { addSuffix: true });
}

function wasEdited(note: ExperimentNote): boolean {
  return note?.updated_at !== "" && note?.created_at !== "" && note.updated_at !== note.created_at;
}

type NoteFormProps = {
  experimentId: number;
  onSubmit: (_comment: string) => Promise<void> | void;
  onCancel?: () => void;
  initialValue?: string;
  placeholder?: string;
  submitLabel?: string;
  isLoading?: boolean;
  cancelMode?: "dirty" | "always";
  startCollapsed?: boolean;
};

function NoteForm({
  experimentId,
  onSubmit,
  onCancel,
  initialValue = "",
  placeholder = "Add a comment...",
  submitLabel = "Send",
  isLoading = false,
  cancelMode = "dirty",
  startCollapsed = false,
}: NoteFormProps) {
  const [comment, setComment] = useState(initialValue);
  const [editorResetKey, setEditorResetKey] = useState(0);
  const [isExpanded, setIsExpanded] = useState(!startCollapsed);
  const [pendingImageUploads, setPendingImageUploads] = useState(0);
  const pendingImageUploadsRef = useRef(0);

  const handlePendingImageUploadsChange = useCallback((pendingUploads: number) => {
    pendingImageUploadsRef.current = pendingUploads;
    setPendingImageUploads(pendingUploads);
  }, []);

  const resetComment = () => {
    setComment(initialValue);
    handlePendingImageUploadsChange(0);
    setEditorResetKey(key => key + 1);
    if (startCollapsed) {
      setIsExpanded(false);
    }
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    const trimmedComment = comment.trim();
    if (!trimmedComment) {
      return;
    }

    if (pendingImageUploadsRef.current > 0) {
      const { toast } = await import("sonner");
      toast.info("Image upload is still in progress");
      return;
    }

    if (hasLocalImageReference(trimmedComment)) {
      const { toast } = await import("sonner");
      toast.error("Image upload has not finished", {
        description: "Wait for the image upload to finish, then submit the note again.",
      });
      return;
    }

    await onSubmit(trimmedComment);

    if (!initialValue) {
      resetComment();
    }
  };

  const handleCancel = () => {
    resetComment();
    onCancel?.();
  };

  const hasComment = comment.trim().length > 0;
  const showCancel = startCollapsed || cancelMode === "always" || hasComment;

  if (!isExpanded) {
    return (
      <Input
        type="text"
        readOnly
        disabled={isLoading}
        placeholder={placeholder}
        aria-label={placeholder}
        onClick={() => setIsExpanded(true)}
        onFocus={() => setIsExpanded(true)}
        className="cursor-text"
      />
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-2">
      <Editor
        key={editorResetKey}
        value={comment}
        onChange={setComment}
        readOnly={isLoading}
        placeholder={placeholder}
        experimentId={experimentId.toString()}
        onPendingImageUploadsChange={handlePendingImageUploadsChange}
        editorClassName="min-h-[140px]"
      />
      <div className="flex justify-end gap-2">
        {pendingImageUploads > 0 && (
          <span className="self-center text-xs text-muted-foreground">Uploading image...</span>
        )}
        {showCancel && (
          <Button type="button" variant="ghost" size="sm" onClick={handleCancel} disabled={isLoading}>
            <X className="h-4 w-4 mr-1" />
            Cancel
          </Button>
        )}
        <Button type="submit" size="sm" disabled={!hasComment || isLoading || pendingImageUploads > 0}>
          <Send className="h-4 w-4 mr-1" />
          {submitLabel}
        </Button>
      </div>
    </form>
  );
}

type NoteItemProps = {
  note: ExperimentNote;
  experimentId: number;
  currentUserId: number | undefined;
  depth?: number;
  onReplySuccess?: () => void;
};

function NoteItem({ note, experimentId, currentUserId, depth = 0, onReplySuccess }: NoteItemProps) {
  const [isReplying, setIsReplying] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  const { trigger: createNote, isMutating: isCreating } = useCreateExperimentNote(experimentId);
  const { trigger: updateNote, isMutating: isUpdating } = useUpdateExperimentNote(experimentId);
  const { trigger: deleteNote, isMutating: isDeleting } = useDeleteExperimentNote(experimentId);

  const isAuthor = currentUserId === note.author_id;
  const authorName = note.author?.name || "Unknown User";

  const handleReply = async (comment: string) => {
    if (!currentUserId) return;
    try {
      await createNote({
        comment,
        parent_id: note.id,
      });
      setIsReplying(false);
      onReplySuccess?.();
    } catch (error) {
      const { toast } = await import("sonner");
      toast.error("Failed to add reply", { description: getErrorMessage(error) });
    }
  };

  const handleEdit = async (comment: string) => {
    try {
      await updateNote({
        noteId: note.id,
        data: { comment },
      });
      setIsEditing(false);
    } catch (error) {
      const { toast } = await import("sonner");
      toast.error("Failed to update note", { description: getErrorMessage(error) });
    }
  };

  const handleDelete = async () => {
    if (window.confirm("Are you sure you want to delete this comment?")) {
      try {
        await deleteNote(note.id);
      } catch (error) {
        const { toast } = await import("sonner");
        toast.error("Failed to delete note", { description: getErrorMessage(error) });
      }
    }
  };

  const indentLevel = Math.min(depth, 4);

  return (
    <div id={`note-${note.id}`} className={cn("relative", indentLevel > 0 && "ml-8 mt-3")}>
      {indentLevel > 0 && <div className="absolute -left-4 top-0 bottom-0 w-px bg-border" />}
      {indentLevel > 0 && <CornerDownRight className="absolute -left-5 top-2 h-4 w-4 text-muted-foreground" />}

      <div className="flex gap-3">
        <Avatar className="h-8 w-8 shrink-0">
          <AvatarFallback className="text-xs">{getInitials(authorName)}</AvatarFallback>
        </Avatar>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-medium text-sm">{authorName}</span>
            <span className="text-xs text-muted-foreground">{formatTimestamp(note.created_at)}</span>
            {wasEdited(note) && <span className="text-xs text-muted-foreground italic">(edited)</span>}
          </div>

          {isEditing ? (
            <div className="mt-2">
              <NoteForm
                experimentId={experimentId}
                initialValue={note.comment || ""}
                onSubmit={handleEdit}
                onCancel={() => setIsEditing(false)}
                submitLabel="Save"
                isLoading={isUpdating}
                cancelMode="always"
              />
            </div>
          ) : (
            <Editor
              value={note.comment || ""}
              readOnly
              experimentId={experimentId.toString()}
              className="mt-1"
              editorClassName="min-h-0"
            />
          )}

          {!isEditing && (
            <div className="flex items-center gap-2 mt-2">
              <Button
                variant="ghost"
                size="sm"
                className="h-7 px-2 text-muted-foreground hover:text-foreground"
                onClick={() => setIsReplying(!isReplying)}
              >
                <Reply className="h-3 w-3 mr-1" />
                Reply
              </Button>

              {isAuthor && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 w-7 p-0 text-muted-foreground hover:text-foreground"
                    >
                      <MoreHorizontal className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start">
                    <DropdownMenuItem onClick={() => setIsEditing(true)}>
                      <Edit2 className="h-4 w-4 mr-2" />
                      Edit
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={handleDelete}
                      disabled={isDeleting}
                      className="text-destructive focus:text-destructive"
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      Delete
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>
          )}

          {isReplying && (
            <div className="mt-3">
              <NoteForm
                experimentId={experimentId}
                onSubmit={handleReply}
                onCancel={() => setIsReplying(false)}
                placeholder={`Reply to ${authorName}...`}
                submitLabel="Reply"
                isLoading={isCreating}
                cancelMode="always"
              />
            </div>
          )}
        </div>
      </div>

      {note.replies && note.replies.length > 0 && (
        <div className="mt-3">
          {note.replies.map(reply => (
            <NoteItem
              key={reply.id}
              note={reply}
              experimentId={experimentId}
              currentUserId={currentUserId}
              depth={depth + 1}
              onReplySuccess={onReplySuccess}
            />
          ))}
        </div>
      )}
    </div>
  );
}

type ExperimentNotesProps = {
  experimentId: number;
};

export function ExperimentNotes({ experimentId }: ExperimentNotesProps) {
  const { data: notes, isLoading, mutate } = useExperimentNotes(experimentId);
  const { data: userData } = useUser();
  const { hash, key } = useLocation();

  useEffect(() => {
    if (!notes?.length || !hash) return;
    const match = hash.match(/^#note-(\d+)$/);
    if (!match) return;
    const el = document.getElementById(`note-${match[1]}`);
    if (!el) return;
    requestAnimationFrame(() => {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      el.classList.remove("note-highlight");
      void el.offsetWidth; // force reflow to restart animation
      el.classList.add("note-highlight");
      el.addEventListener("animationend", () => el.classList.remove("note-highlight"), { once: true });
    });
  }, [notes, hash, key]);
  const { trigger: createNote, isMutating: isCreating } = useCreateExperimentNote(experimentId);

  const currentUserName = userData?.user?.name;
  const currentUserId = userData?.user?.id;

  const handleCreateNote = async (comment: string) => {
    if (!currentUserId) {
      const { toast } = await import("sonner");
      toast.error("Cannot add note", { description: "Your user account is not set up in the system." });
      return;
    }
    try {
      await createNote({
        comment,
      });
      mutate();
    } catch (error) {
      const { toast } = await import("sonner");
      toast.error("Failed to add note", { description: getErrorMessage(error) });
    }
  };

  const userNotFound = userData?.user && !currentUserId;

  return (
    <section>
      <header className="mb-2 flex items-center gap-2 border-b border-border pb-2">
        <MessageSquare className="h-5 w-5" />
        <h3 className="text-lg font-semibold tracking-tight">Notes</h3>
      </header>
      <p className="mb-6 text-sm text-muted-foreground">Discussion and comments about this experiment.</p>
      <div className="space-y-6">
        {userNotFound && (
          <div className="text-sm text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-950/20 p-3 rounded-md">
            Your user account ({currentUserName}) is not set up in the system. Please contact an administrator to add
            your account before you can post notes.
          </div>
        )}

        <NoteForm
          experimentId={experimentId}
          onSubmit={handleCreateNote}
          placeholder="Add a note..."
          isLoading={isCreating || !currentUserId}
          startCollapsed
        />

        {isLoading ? (
          <div className="text-sm text-muted-foreground">Loading notes...</div>
        ) : (
          notes &&
          notes.length > 0 && (
            <div className="space-y-4 pt-4 border-t">
              {notes.map(note => (
                <NoteItem
                  key={note.id}
                  note={note}
                  experimentId={experimentId}
                  currentUserId={currentUserId}
                  onReplySuccess={() => mutate()}
                />
              ))}
            </div>
          )
        )}
      </div>
    </section>
  );
}
